import { useState, useEffect } from "react";
import { adminFetch } from "../../lib/api";

type SortBy = "newest" | "oldest" | "name";

interface ThirdPartyService {
  id: string;
  name: string;
  region: string;
  status: string;
  status_url: string;
  iam_client_id: string;
  iam_client_secret_set: boolean;
  iam_token_url: string;
  health_url: string;
  health_headers: Record<string, string>;
  last_health_response: Record<string, unknown> | null;
  last_health_checked_at: string | null;
  last_health_error: string;
  check_interval_seconds: number;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

const STATUS_OPTIONS = [
  { value: "operational", label: "Operational" },
  { value: "degraded", label: "Degraded" },
  { value: "outage", label: "Outage" },
  { value: "maintenance", label: "Maintenance" },
];

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`ac-badge ac-badge--${status}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function HealthSummary({ response }: { response: Record<string, unknown> | null }) {
  if (!response) return <span className="ac-td--muted">—</span>;
  const services = response.services as Record<string, { status: string }> | undefined;
  if (!services) return <span className="ac-td--muted">—</span>;
  return (
    <div style={{ fontSize: "0.72rem", lineHeight: 1.6 }}>
      {Object.entries(services).map(([svcName, svcData]) => (
        <div key={svcName} style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
          <span
            className={`sp-status-icon sp-status-icon--${svcData.status === "ok" ? "operational" : "degraded"}`}
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          />
          <span style={{ textTransform: "capitalize" }}>{svcName}</span>
        </div>
      ))}
    </div>
  );
}

export function ThirdPartyServicesPage() {
  const [services, setServices] = useState<ThirdPartyService[]>([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>("newest");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showDialog, setShowDialog] = useState(false);
  const [editTarget, setEditTarget] = useState<ThirdPartyService | null>(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [dialogHealthResult, setDialogHealthResult] = useState<Record<string, unknown> | null>(null);
  const [dialogHealthError, setDialogHealthError] = useState("");
  const [tableCheckingId, setTableCheckingId] = useState<string | null>(null);

  // Form state
  const [fName, setFName] = useState("");
  const [fRegion, setFRegion] = useState("");
  const [fStatus, setFStatus] = useState("operational");
  const [fStatusUrl, setFStatusUrl] = useState("");
  const [fIamClientId, setFIamClientId] = useState("");
  const [fIamClientSecret, setFIamClientSecret] = useState("");
  const [fIamTokenUrl, setFIamTokenUrl] = useState("");
  const [fHealthUrl, setFHealthUrl] = useState("");
  // Health headers: array of {key, value} pairs for the editor
  const [fHealthHeaders, setFHealthHeaders] = useState<{ key: string; value: string }[]>([]);
  const [fCheckInterval, setFCheckInterval] = useState(300);
  const [fOrder, setFOrder] = useState(0);
  const [fActive, setFActive] = useState(true);

  async function loadServices() {
    const r = await adminFetch("/api/v1/admin/third-party-services/");
    if (r.ok) setServices(await r.json());
  }

  useEffect(() => {
    loadServices();
  }, []);

  function resetForm() {
    setFName("");
    setFRegion("");
    setFStatus("operational");
    setFStatusUrl("");
    setFIamClientId("");
    setFIamClientSecret("");
    setFIamTokenUrl("");
    setFHealthUrl("");
    setFHealthHeaders([]);
    setFCheckInterval(300);
    setFOrder(0);
    setFActive(true);
    setError("");
    setDialogHealthResult(null);
    setDialogHealthError("");
  }

  function openCreate() {
    resetForm();
    setEditTarget(null);
    setShowDialog(true);
  }

  function openEdit(svc: ThirdPartyService) {
    setFName(svc.name);
    setFRegion(svc.region);
    setFStatus(svc.status);
    setFStatusUrl(svc.status_url);
    setFIamClientId(svc.iam_client_id);
    setFIamClientSecret("");
    setFIamTokenUrl(svc.iam_token_url);
    setFHealthUrl(svc.health_url);
    setFHealthHeaders(
      Object.entries(svc.health_headers ?? {}).map(([key, value]) => ({ key, value }))
    );
    setFCheckInterval(svc.check_interval_seconds ?? 300);
    setFOrder(svc.display_order);
    setFActive(svc.is_active);
    setError("");
    setDialogHealthResult(svc.last_health_response);
    setDialogHealthError(svc.last_health_error);
    setEditTarget(svc);
    setShowDialog(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!fName.trim()) {
      setError("Service name is required.");
      return;
    }
    setSubmitting(true);
    setError("");

    const body: Record<string, unknown> = {
      name: fName.trim(),
      region: fRegion.trim(),
      status: fStatus,
      status_url: fStatusUrl.trim(),
      iam_client_id: fIamClientId.trim(),
      iam_token_url: fIamTokenUrl.trim(),
      health_url: fHealthUrl.trim(),
      health_headers: Object.fromEntries(
        fHealthHeaders.filter((h) => h.key.trim()).map((h) => [h.key.trim(), h.value.trim()])
      ),
      check_interval_seconds: fCheckInterval,
      display_order: fOrder,
      is_active: fActive,
    };
    if (fIamClientSecret.trim()) {
      body.iam_client_secret = fIamClientSecret.trim();
    }

    try {
      const isEdit = editTarget !== null;
      const url = isEdit
        ? `/api/v1/admin/third-party-services/${editTarget!.id}/`
        : "/api/v1/admin/third-party-services/";
      const r = await adminFetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.name?.[0] || d.detail || `HTTP ${r.status}`);
      }
      setShowDialog(false);
      resetForm();
      await loadServices();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save.");
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * "Check" button inside the dialog — saves credentials first (POST/PATCH),
   * then immediately triggers a health check and shows the result inline.
   */
  async function handleDialogCheck() {
    if (!fIamTokenUrl.trim() || !fHealthUrl.trim()) {
      setDialogHealthError("IAM Token URL and AIS Health URL are required to run a check.");
      setDialogHealthResult(null);
      return;
    }
    setCheckingHealth(true);
    setDialogHealthResult(null);
    setDialogHealthError("");
    setError("");

    // Persist first so the server has the credentials to use
    const body: Record<string, unknown> = {
      name: fName.trim() || "Unnamed",
      region: fRegion.trim(),
      status: fStatus,
      status_url: fStatusUrl.trim(),
      iam_client_id: fIamClientId.trim(),
      iam_token_url: fIamTokenUrl.trim(),
      health_url: fHealthUrl.trim(),
      health_headers: Object.fromEntries(
        fHealthHeaders.filter((h) => h.key.trim()).map((h) => [h.key.trim(), h.value.trim()])
      ),
      check_interval_seconds: fCheckInterval,
      display_order: fOrder,
      is_active: fActive,
    };
    if (fIamClientSecret.trim()) {
      body.iam_client_secret = fIamClientSecret.trim();
    }

    try {
      let svcId: string;
      if (editTarget) {
        const r = await adminFetch(`/api/v1/admin/third-party-services/${editTarget.id}/`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!r.ok) throw new Error(`Save failed: HTTP ${r.status}`);
        const saved: ThirdPartyService = await r.json();
        svcId = saved.id;
      } else {
        if (!fName.trim()) {
          setDialogHealthError("Enter a service name before checking health.");
          setCheckingHealth(false);
          return;
        }
        const r = await adminFetch("/api/v1/admin/third-party-services/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!r.ok) throw new Error(`Save failed: HTTP ${r.status}`);
        const saved: ThirdPartyService = await r.json();
        svcId = saved.id;
        setEditTarget(saved);
      }

      const hr = await adminFetch(`/api/v1/admin/third-party-services/${svcId}/check-health/`, {
        method: "POST",
      });
      const hData: ThirdPartyService = await hr.json();
      if (!hr.ok) {
        setDialogHealthError(
          (hData as unknown as { detail?: string }).detail || `Health check failed: HTTP ${hr.status}`
        );
      } else {
        setDialogHealthResult(hData.last_health_response);
        setDialogHealthError(hData.last_health_error);
        setFStatus(hData.status);
      }
      await loadServices();
    } catch (err) {
      setDialogHealthError(err instanceof Error ? err.message : "Health check failed.");
    } finally {
      setCheckingHealth(false);
    }
  }

  async function handleTableCheckHealth(id: string) {
    setTableCheckingId(id);
    try {
      const r = await adminFetch(`/api/v1/admin/third-party-services/${id}/check-health/`, {
        method: "POST",
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(d.detail || `Health check failed (HTTP ${r.status})`);
      }
      await loadServices();
    } finally {
      setTableCheckingId(null);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this third-party service? This cannot be undone.")) return;
    const r = await adminFetch(`/api/v1/admin/third-party-services/${id}/`, { method: "DELETE" });
    if (r.ok) await loadServices();
  }

  const filtered = services
    .filter((s) => {
      if (search) {
        const q = search.toLowerCase();
        if (!s.name.toLowerCase().includes(q) && !s.region.toLowerCase().includes(q)) return false;
      }
      if (statusFilter !== "All" && s.status !== statusFilter.toLowerCase()) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "newest") return b.created_at.localeCompare(a.created_at);
      if (sortBy === "oldest") return a.created_at.localeCompare(b.created_at);
      return a.name.localeCompare(b.name);
    });

  const healthServices =
    dialogHealthResult &&
    typeof dialogHealthResult.services === "object" &&
    dialogHealthResult.services !== null
      ? Object.entries(dialogHealthResult.services as Record<string, { status: string }>)
      : null;

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Third-Party Services</h1>
        </div>
        <div className="ac-page-header__actions">
          <button type="button" className="aptean-btn aptean-btn--primary" onClick={openCreate}>
            Add service
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="ac-filter-bar" style={{ gap: "var(--aptean-space-3)" }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 280 }}>
          <input
            type="text"
            className="aptean-input"
            placeholder="Search services"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="aptean-input"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortBy)}
          style={{ width: 140 }}
          aria-label="Sort by"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="name">Name</option>
        </select>
        <select
          className="aptean-input"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ width: 140 }}
          aria-label="Filter by status"
        >
          <option value="All">All statuses</option>
          <option value="operational">Operational</option>
          <option value="degraded">Degraded</option>
          <option value="outage">Outage</option>
          <option value="maintenance">Maintenance</option>
        </select>
      </div>

      {/* Table */}
      <div className="ac-table-wrap">
        <table className="ac-table">
          <thead>
            <tr>
              <th style={{ width: "18%" }}>Service</th>
              <th style={{ width: "12%" }}>Region</th>
              <th style={{ width: "10%" }}>Status</th>
              <th style={{ width: "10%" }}>IAM</th>
              <th>Sub-service health</th>
              <th style={{ width: "14%" }}>Last checked</th>
              <th style={{ width: "6%" }}>Active</th>
              <th className="ac-th--right" style={{ width: "16%" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <div className="ac-empty">
                    <div className="ac-empty__title">No third-party services</div>
                    <div className="ac-empty__body">Add external services like Azure, AWS, etc. to track their health.</div>
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((svc) => (
                <tr key={svc.id}>
                  <td><div className="ac-row-title">{svc.name}</div></td>
                  <td className="ac-td--muted">{svc.region || "—"}</td>
                  <td><StatusBadge status={svc.status} /></td>
                  <td className="ac-td--muted" style={{ fontSize: "0.75rem" }}>
                    {svc.iam_client_secret_set ? (
                      <span style={{ color: "var(--aptean-success, #22c55e)" }}>Configured</span>
                    ) : (
                      <span style={{ color: "var(--aptean-muted, #888)" }}>Not set</span>
                    )}
                  </td>
                  <td>
                    {svc.last_health_error ? (
                      <span style={{ color: "var(--aptean-danger, #ef4444)", fontSize: "0.72rem" }}>
                        {svc.last_health_error}
                      </span>
                    ) : (
                      <HealthSummary response={svc.last_health_response} />
                    )}
                  </td>
                  <td className="ac-td--muted" style={{ fontSize: "0.72rem" }}>
                    {svc.last_health_checked_at
                      ? new Date(svc.last_health_checked_at).toLocaleString()
                      : "Never"}
                  </td>
                  <td className="ac-td--muted">{svc.is_active ? "Yes" : "No"}</td>
                  <td className="ac-td--actions ac-td--right">
                    <div style={{ display: "flex", gap: "0.4rem", justifyContent: "flex-end" }}>
                      <button
                        type="button"
                        className="aptean-btn aptean-btn--ghost"
                        style={{ fontSize: "0.7rem", padding: "0.25rem 0.6rem", whiteSpace: "nowrap" }}
                        onClick={() => handleTableCheckHealth(svc.id)}
                        disabled={tableCheckingId === svc.id || !svc.iam_client_secret_set}
                        title={!svc.iam_client_secret_set ? "Configure IAM credentials first" : "Run health check"}
                      >
                        {tableCheckingId === svc.id ? "Checking…" : "Check health"}
                      </button>
                      <button
                        type="button"
                        className="aptean-btn aptean-btn--ghost"
                        style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                        onClick={() => openEdit(svc)}
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className="aptean-btn aptean-btn--danger"
                        style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                        onClick={() => handleDelete(svc.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Dialog */}
      {showDialog && (
        <div className="ac-overlay" onClick={(e) => e.target === e.currentTarget && setShowDialog(false)}>
          <div className="ac-dialog" style={{ maxWidth: 560, display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">
                {editTarget ? "Edit" : "Add"} Third-Party Service
              </h2>
              <button
                type="button"
                className="ac-dialog__close"
                onClick={() => setShowDialog(false)}
                aria-label="Close"
              >
                &#10005;
              </button>
            </div>

            <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <div className="ac-dialog__body" style={{ overflowY: "auto", flex: 1 }}>
                {error && <div className="ac-alert ac-alert--error">{error}</div>}

                {/* ── Basic info ── */}
                <div className="ac-field">
                  <label htmlFor="tp-name">Service name *</label>
                  <input
                    id="tp-name"
                    type="text"
                    className="aptean-input"
                    value={fName}
                    onChange={(e) => setFName(e.target.value)}
                    required
                    placeholder="e.g. Azure OpenAI"
                  />
                </div>
                <div className="ac-field">
                  <label htmlFor="tp-region">Region</label>
                  <input
                    id="tp-region"
                    type="text"
                    className="aptean-input"
                    value={fRegion}
                    onChange={(e) => setFRegion(e.target.value)}
                    placeholder="e.g. East US, Germany West Central"
                  />
                </div>
                {/* ── IAM / Health credentials ── */}
                <hr style={{ margin: "1rem 0", borderColor: "var(--aptean-border, #e5e7eb)" }} />
                <p style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: "0.75rem" }}>
                  Health check credentials
                </p>

                <div className="ac-field">
                  <label htmlFor="tp-iam-client-id">IAM Client ID</label>
                  <input
                    id="tp-iam-client-id"
                    type="text"
                    className="aptean-input"
                    value={fIamClientId}
                    onChange={(e) => setFIamClientId(e.target.value)}
                    placeholder="e.g. CKH3BY3FOGH20VEBH"
                    autoComplete="off"
                  />
                </div>
                <div className="ac-field">
                  <label htmlFor="tp-iam-secret">
                    IAM Client Secret
                    {editTarget?.iam_client_secret_set && (
                      <span style={{ marginLeft: "0.5rem", fontSize: "0.72rem", color: "var(--aptean-muted, #888)" }}>
                        (leave blank to keep existing)
                      </span>
                    )}
                  </label>
                  <input
                    id="tp-iam-secret"
                    type="password"
                    className="aptean-input"
                    value={fIamClientSecret}
                    onChange={(e) => setFIamClientSecret(e.target.value)}
                    placeholder={editTarget?.iam_client_secret_set ? "••••••••" : "Paste secret here"}
                    autoComplete="new-password"
                  />
                </div>
                <div className="ac-field">
                  <label htmlFor="tp-iam-token-url">IAM Token URL</label>
                  <input
                    id="tp-iam-token-url"
                    type="url"
                    className="aptean-input"
                    value={fIamTokenUrl}
                    onChange={(e) => setFIamTokenUrl(e.target.value)}
                    placeholder="https://…/protocol/openid-connect/token"
                  />
                </div>
                <div className="ac-field">
                  <label htmlFor="tp-health-url">AIS Health URL</label>
                  <input
                    id="tp-health-url"
                    type="url"
                    className="aptean-input"
                    value={fHealthUrl}
                    onChange={(e) => setFHealthUrl(e.target.value)}
                    placeholder="https://…/ais/health/detailed"
                  />
                </div>

                {/* ── Health request headers ── */}
                <div className="ac-field">
                  <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Health request headers</span>
                    <button
                      type="button"
                      className="aptean-btn aptean-btn--ghost"
                      style={{ fontSize: "0.72rem", padding: "0.15rem 0.6rem" }}
                      onClick={() => setFHealthHeaders((prev) => [...prev, { key: "", value: "" }])}
                    >
                      + Add header
                    </button>
                  </label>
                  {fHealthHeaders.length === 0 ? (
                    <p style={{ margin: "0.25rem 0 0", fontSize: "0.75rem", color: "var(--aptean-muted, #888)" }}>
                      No custom headers. Click "+ Add header" to add e.g. X-APTEAN-REGION.
                    </p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.4rem" }}>
                      {fHealthHeaders.map((header, idx) => (
                        <div key={idx} style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                          <input
                            type="text"
                            className="aptean-input"
                            style={{ flex: "0 0 44%" }}
                            value={header.key}
                            onChange={(e) =>
                              setFHealthHeaders((prev) =>
                                prev.map((h, i) => i === idx ? { ...h, key: e.target.value } : h)
                              )
                            }
                            placeholder="Header name"
                            aria-label={`Header ${idx + 1} name`}
                          />
                          <input
                            type="text"
                            className="aptean-input"
                            style={{ flex: 1 }}
                            value={header.value}
                            onChange={(e) =>
                              setFHealthHeaders((prev) =>
                                prev.map((h, i) => i === idx ? { ...h, value: e.target.value } : h)
                              )
                            }
                            placeholder="Value"
                            aria-label={`Header ${idx + 1} value`}
                          />
                          <button
                            type="button"
                            className="aptean-btn aptean-btn--danger"
                            style={{ fontSize: "0.75rem", padding: "0.3rem 0.6rem", flexShrink: 0 }}
                            onClick={() =>
                              setFHealthHeaders((prev) => prev.filter((_, i) => i !== idx))
                            }
                            aria-label="Remove header"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── Inline health result ── */}
                {(dialogHealthResult || dialogHealthError) && (
                  <div
                    style={{
                      margin: "0.75rem 0",
                      padding: "0.75rem 1rem",
                      borderRadius: 6,
                      background: dialogHealthError
                        ? "var(--aptean-danger-bg, #fef2f2)"
                        : "var(--aptean-success-bg, #f0fdf4)",
                      border: `1px solid ${dialogHealthError ? "var(--aptean-danger, #ef4444)" : "var(--aptean-success, #22c55e)"}`,
                      fontSize: "0.78rem",
                    }}
                  >
                    {dialogHealthError ? (
                      <p style={{ color: "var(--aptean-danger, #ef4444)", margin: 0 }}>
                        {dialogHealthError}
                      </p>
                    ) : (
                      <>
                        <p style={{ margin: "0 0 0.4rem", fontWeight: 600, color: "var(--aptean-success, #22c55e)" }}>
                          Health check passed
                        </p>
                        {healthServices && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem 1.2rem" }}>
                            {healthServices.map(([name, data]) => (
                              <span key={name} style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                                <span
                                  className={`sp-status-icon sp-status-icon--${data.status === "ok" ? "operational" : "degraded"}`}
                                  aria-hidden="true"
                                />
                                <span style={{ textTransform: "capitalize" }}>{name}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* ── Other settings ── */}
                <hr style={{ margin: "1rem 0", borderColor: "var(--aptean-border, #e5e7eb)" }} />
                <div className="ac-field">
                  <label htmlFor="tp-status">Status (fallback when no health check is configured)</label>
                  <select
                    id="tp-status"
                    className="aptean-input"
                    value={fStatus}
                    onChange={(e) => setFStatus(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="ac-field">
                  <label htmlFor="tp-interval">Check interval (seconds)</label>
                  <input
                    id="tp-interval"
                    type="number"
                    className="aptean-input"
                    value={fCheckInterval}
                    onChange={(e) => setFCheckInterval(Math.max(0, Number(e.target.value)))}
                    min={0}
                    step={60}
                    placeholder="300 (0 = disabled)"
                  />
                </div>
                <div className="ac-field">
                  <label htmlFor="tp-order">Display order</label>
                  <input
                    id="tp-order"
                    type="number"
                    className="aptean-input"
                    value={fOrder}
                    onChange={(e) => setFOrder(Number(e.target.value))}
                    min={0}
                  />
                </div>
                <div className="ac-field">
                  <label className="subscribe-checkbox">
                    <input
                      type="checkbox"
                      checked={fActive}
                      onChange={(e) => setFActive(e.target.checked)}
                    />
                    Active (visible on status page)
                  </label>
                </div>
              </div>

              {/* ── Footer: Check | Cancel | Save ── */}
              <div className="ac-dialog__footer" style={{ justifyContent: "space-between" }}>
                <button
                  type="button"
                  className="aptean-btn aptean-btn--secondary"
                  onClick={handleDialogCheck}
                  disabled={checkingHealth || submitting}
                  title="Save credentials then run a live health check"
                >
                  {checkingHealth ? "Checking…" : "Check"}
                </button>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button
                    type="button"
                    className="aptean-btn aptean-btn--ghost"
                    onClick={() => setShowDialog(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="aptean-btn aptean-btn--primary"
                    disabled={submitting || checkingHealth}
                  >
                    {submitting ? "Saving…" : editTarget ? "Save changes" : "Add service"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
